import { DateTime } from "luxon";
import { OptimizedGantt } from "@planzone/dto";
import { HOURS_PER_DAY } from "@scenes/optimizedGantt/api/views";
import { State } from "../context/types";
import { isDone, isMilestone, isTask } from "../utils";
import { DateRange } from "./types";

type Config = {
  cell: {
    width: number;
    height: number;
  };
};

function calculateStartAndEnd(
  element: OptimizedGantt[number],
  completionDate: DateTime,
  range: DateRange,
  areDerivationsToggled: boolean,
): DateRange {
  const isCompleted =
    isDone(element) && (isTask(element) || isMilestone(element));

  const startDate =
    isCompleted && completionDate < range.startDate
      ? completionDate
          .minus({ days: isMilestone(element) ? 0 : 1 })
          .startOf("day")
      : range.startDate;

  const endDate =
    isCompleted && !areDerivationsToggled ? completionDate : range.endDate;

  return { startDate, endDate };
}

// Day views snap interactions to whole days (one cell); the daily view
// snaps to hours (one 24th of a cell).
function getSnapQuantum(cell: Config["cell"], isDaily: boolean): number {
  return isDaily ? cell.width / HOURS_PER_DAY : cell.width;
}

function calculateWidth(
  cell: Config["cell"],
  offset: number,
  dayDiff: number,
  resize: State["resize"],
  element: OptimizedGantt[number],
  isDaily: boolean,
): number {
  const quantum = getSnapQuantum(cell, isDaily);

  // Day views render an INCLUSIVE end (+1 day: the end day is lit whole);
  // the daily view's dayDiff is measured to an exclusive end instant, so no
  // +1 — zero-duration elements (milestones, instants) get one hour slot.
  return resize.right.isResizing
    ? Math.round(resize.right.offset / quantum) * quantum - offset * cell.width
    : resize.left.isResizing
      ? isDaily
        ? (offset + dayDiff) * cell.width -
          Math.round(resize.left.offset / quantum) * quantum
        : (offset + 1) * cell.width -
          Math.round(resize.left.offset / cell.width) * cell.width +
          cell.width * dayDiff
      : cell.width * (dayDiff + (!isDaily && !isMilestone(element) ? 1 : 0)) ||
        quantum;
}

function calculateLeftMargin(
  cell: Config["cell"],
  drag: { isDragging: boolean; offset: number; clickInitialPos: number },
  resize: State["resize"],
  offset: number,
  isDaily: boolean,
): number {
  const quantum = getSnapQuantum(cell, isDaily);

  return drag.isDragging
    ? Math.round((drag.offset - drag.clickInitialPos) / quantum) * quantum
    : resize.left.isResizing
      ? Math.round(resize.left.offset / quantum) * quantum
      : offset * cell.width;
}

export {
  calculateLeftMargin,
  calculateStartAndEnd,
  calculateWidth,
  getSnapQuantum,
};
